import { pickT } from "@repo/shared";
import type { PdfCurrencyDisplay } from "@repo/shared/types";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { useT } from "@/lib/i18n/use-t";
import { CurrencyDisplayField } from "./currency-display-field";
import { T } from "./expected-value-form.i18n";
import type { ComponentFormProps } from "./types";

export type ExpectedValueConfig = {
  title?: string;
  description?: string;
  /** QL-414 — absent = the block's default. */
  currencyDisplay?: PdfCurrencyDisplay;
  showChart: boolean;
  showSummaryTable: boolean;
  compareWithBank?: boolean;
};

type ExpectedValueFormProps = ComponentFormProps<ExpectedValueConfig>;

export function ExpectedValueForm({ config, onChange }: ExpectedValueFormProps) {
  const t = useT(T);

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="expected-value-title">{t.title}</Label>
        <Input
          id="expected-value-title"
          value={config.title || ""}
          onChange={(e) => onChange({ ...config, title: e.target.value })}
          placeholder={t.titlePlaceholder}
        />
        <p className="text-xs text-muted-foreground">{t.titleHelp}</p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="expected-value-description">{t.description}</Label>
        <Textarea
          id="expected-value-description"
          value={config.description || ""}
          onChange={(e) => onChange({ ...config, description: e.target.value })}
          placeholder={t.descriptionPlaceholder}
          rows={2}
        />
        <p className="text-xs text-muted-foreground">{t.descriptionHelp}</p>
      </div>

      <CurrencyDisplayField
        blockType="expected_value"
        value={config.currencyDisplay}
        chartOnly={config.showChart !== false && !config.showSummaryTable}
        onChange={(currencyDisplay) => onChange({ ...config, currencyDisplay })}
      />

      <div className="flex items-center justify-between">
        <div>
          <Label htmlFor="expected-value-show-chart" className="text-base">
            {t.showChart}
          </Label>
          <p className="text-sm text-muted-foreground">{t.showChartHelp}</p>
        </div>
        <Switch
          id="expected-value-show-chart"
          checked={config.showChart !== false}
          onCheckedChange={(checked) => onChange({ ...config, showChart: checked })}
        />
      </div>

      <div className="flex items-center justify-between">
        <div>
          <Label htmlFor="expected-value-show-summary-table" className="text-base">
            {t.showSummaryTable}
          </Label>
          <p className="text-sm text-muted-foreground">{t.showSummaryTableHelp}</p>
        </div>
        <Switch
          id="expected-value-show-summary-table"
          checked={config.showSummaryTable || false}
          onCheckedChange={(checked) => onChange({ ...config, showSummaryTable: checked })}
        />
      </div>

      <div className="flex items-center justify-between">
        <div>
          <Label htmlFor="expected-value-compare-with-bank" className="text-base">
            {t.compareWithBank}
          </Label>
          <p className="text-sm text-muted-foreground">{t.compareWithBankHelp}</p>
        </div>
        <Switch
          id="expected-value-compare-with-bank"
          checked={config.compareWithBank === true}
          onCheckedChange={(checked) => onChange({ ...config, compareWithBank: checked })}
        />
      </div>

      {!config.showChart && !config.showSummaryTable && (
        <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
          {t.validationError}
        </div>
      )}

      <div className="p-3 bg-muted/50 rounded-md">
        <p className="text-xs text-muted-foreground">{t.componentDescription}</p>
      </div>
    </div>
  );
}

export function validateExpectedValueConfig(
  config: ExpectedValueConfig,
  lang = "en_GB",
): string | null {
  const t = pickT(T, lang);
  if (!config.showChart && !config.showSummaryTable) {
    return t.validationError;
  }
  return null;
}

export function getDefaultExpectedValueConfig(): ExpectedValueConfig {
  return {
    title: "Expected Value",
    description: "Projected future value of your investment.",
    showChart: true,
    showSummaryTable: false,
  };
}
